import { useCallback, useContext, useEffect, useMemo, useRef } from "react";

import { ChatContext } from "../../contexts/chat/context";
import { isCommonInventoryHealthCurrentViewReceiptId } from "../../contexts/chat/nucleus_sse_client";
import { LayoutContext } from "../../contexts/layout/context";
import { VersionedDataContext } from "../../contexts/versioned_data/context";
import { useCustomer } from "../../contexts/app/provider";
import {
  createReceiptBoundInventoryHealthCellContext,
  inventoryHealthReceiptCellPrompt,
  isSupportedReceiptBoundInventoryHealthCellRecipe,
  type CommonInventoryHealthCellContextV2,
  type InventoryHealthVariant,
} from "../../entities/inventory_health_cell_context";
import {
  createInventoryHealthUnfulfilledDemandCellContext,
  inventoryHealthUnfulfilledDemandCellPrompt,
  isSupportedInventoryHealthUnfulfilledDemandCellContext,
  type CommonInventoryHealthUnfulfilledDemandCellContext,
} from "../../entities/inventory_health_unfulfilled_demand_cell_context";
import type { DatePivotParams } from "../../entities/date_pivot_params";
import {
  renderedViewBindingIdentity,
  type CellExplanationCapture,
  type PivotCellExplanationConfig,
  type RenderedViewBinding,
} from "../../entities/pivot_view_config";
import {
  createCommonViewContext,
  type CommonViewContextV1,
} from "../../entities/common_view_context";
import {
  createRenderedCellContext,
  isSupportedRenderedCellContext,
  renderedCellPrompt,
  type CommonRenderedCellContextV1,
} from "../../entities/rendered_cell_context";
import {
  supportsCommonNucleusInventoryHealthUnfulfilledDemand,
  supportsCommonNucleusInventoryHealthVariant,
} from "../../entities/common_nucleus_customer_capabilities";
import {
  awaitNucleusExplainStart,
  NUCLEUS_EXPLAIN_BUSY_REASON,
  NUCLEUS_EXPLAIN_STALE_REASON,
  rejectNucleusExplainActivation,
} from "../../entities/nucleus_explain_action";
import { useSynchronizedPending } from "../../components/nucleus_explain/use_synchronized_pending";

type PendingInventoryHealthCell = Readonly<{
  context:
    | CommonInventoryHealthCellContextV2
    | CommonInventoryHealthUnfulfilledDemandCellContext
    | CommonRenderedCellContextV1;
  viewContext?: CommonViewContextV1;
  prompt: string;
  runIdentity: string;
  sourceIdentity: string;
  connectionReady: boolean;
}>;

const ENDING_INVENTORY_MEASURE = "ending inventory on hand";
const UNFULFILLED_DEMAND_MEASURE = "unfulfilled demand";

const normalizedMeasure = (value: unknown): string | undefined =>
  typeof value === "string" && value.trim().length > 0
    ? value.trim().toLowerCase()
    : undefined;

const isSupportedExplanationContext = (
  context: CellExplanationCapture["metricContext"],
  variant: InventoryHealthVariant,
  tenantId: string,
): context is
  | CommonInventoryHealthCellContextV2
  | CommonInventoryHealthUnfulfilledDemandCellContext
  | CommonRenderedCellContextV1 => {
  if (context.version === "CommonInventoryHealthCellContext.v2") {
    return (
      context.variant === variant &&
      isSupportedReceiptBoundInventoryHealthCellRecipe(context)
    );
  }
  if (
    context.version === "CommonInventoryHealthUnfulfilledDemandCellContext.v1"
  ) {
    return (
      context.variant === variant &&
      isSupportedInventoryHealthUnfulfilledDemandCellContext(context, tenantId)
    );
  }
  return (
    context.version === "CommonRenderedCellContext.v1" &&
    context.variant === variant &&
    isSupportedRenderedCellContext(context)
  );
};

const getRunIdentity = (
  run: React.ContextType<typeof VersionedDataContext>["mainRun"],
): string | undefined =>
  run
    ? [
        run.runSettings.organizationId,
        run.runSettings.inputId,
        run.runSettings.workflowId,
        run.runSettings.settingsNameAndVersion,
        run.runSettings.runAttemptId,
      ].join("\u0000")
    : undefined;

export const useInventoryHealthCellExplanation = (
  variant: InventoryHealthVariant,
): Readonly<{
  cellExplanation: PivotCellExplanationConfig<DatePivotParams> | undefined;
  cellExplanationEnabled: boolean;
  onRenderedSourceChange: (
    source: RenderedViewBinding | undefined,
    viewParams?: DatePivotParams,
  ) => void;
}> => {
  const { selectedCustomerId } = useCustomer();
  const {
    mainRun,
    lastSuccessfulRun,
    comparedRun = null,
  } = useContext(VersionedDataContext);
  const { isComparing, setIsSidebarOpen } = useContext(LayoutContext);
  const {
    connectChat,
    isAwaitingResponse,
    isConnected,
    isConnecting,
    registerCurrentViewData,
    sendMessage,
    transportKind,
  } = useContext(ChatContext);
  const activeRun = mainRun || lastSuccessfulRun;
  const activeRunIdentity = getRunIdentity(activeRun);
  const activeRunIdentityRef = useRef(activeRunIdentity);
  activeRunIdentityRef.current = activeRunIdentity;
  const {
    pending: pendingCell,
    pendingRef: pendingCellRef,
    setPending: setPendingCell,
    clearPending,
    replacePending,
  } = useSynchronizedPending<PendingInventoryHealthCell>();
  const currentRenderedSourceIdentityRef = useRef<string | undefined>(
    undefined,
  );
  const hasRenderedSourceSignalRef = useRef(false);
  const releaseCurrentViewDataRef = useRef<(() => void) | null>(null);

  const supportsCellExplain = Boolean(
    transportKind === "platform" &&
      activeRun &&
      activeRunIdentity &&
      activeRun.status === "RUN_COMPLETED" &&
      activeRun.runSettings.workflowId === "workflow_atomic_pivots" &&
      !isComparing &&
      comparedRun === null &&
      supportsCommonNucleusInventoryHealthVariant(
        activeRun.runSettings.organizationId,
        variant,
      ) &&
      (!selectedCustomerId ||
        selectedCustomerId === activeRun.runSettings.organizationId),
  );
  const supportsCellExplainRef = useRef(supportsCellExplain);
  supportsCellExplainRef.current = supportsCellExplain;

  const handleRenderedSourceChange = useCallback(
    (source: RenderedViewBinding | undefined, viewParams?: DatePivotParams) => {
      hasRenderedSourceSignalRef.current = true;
      const sourceIdentity = source
        ? renderedViewBindingIdentity(source)
        : undefined;
      currentRenderedSourceIdentityRef.current = sourceIdentity;
      const pending = pendingCellRef.current;
      if (pending && pending.sourceIdentity !== sourceIdentity) {
        clearPending(pending);
      }
      releaseCurrentViewDataRef.current?.();
      releaseCurrentViewDataRef.current = null;
      const viewContext = viewParams
        ? createCommonViewContext({
            surfaceId: "inventory-health",
            measure: viewParams.measure,
            dateHierarchy: viewParams.dateHierarchy,
            minDate: viewParams.minDate,
            maxDate: viewParams.maxDate,
            segments: viewParams.segments,
            filters: viewParams.filters,
            postFilters: viewParams.postFilters,
          })
        : undefined;
      if (
        supportsCellExplain &&
        registerCurrentViewData &&
        source &&
        isCommonInventoryHealthCurrentViewReceiptId(source.receiptId) &&
        viewParams &&
        viewContext &&
        getRunIdentity(source.run) === activeRunIdentity
      ) {
        releaseCurrentViewDataRef.current = registerCurrentViewData(
          {
            version: "CommonCurrentViewData.v1",
            surfaceId: "inventory-health",
            receiptId: source.receiptId,
          },
          viewContext,
        );
      }
    },
    [
      activeRunIdentity,
      clearPending,
      pendingCellRef,
      registerCurrentViewData,
      supportsCellExplain,
    ],
  );

  useEffect(
    () => () => {
      releaseCurrentViewDataRef.current?.();
      releaseCurrentViewDataRef.current = null;
    },
    [
      activeRunIdentity,
      registerCurrentViewData,
      selectedCustomerId,
      supportsCellExplain,
      variant,
    ],
  );

  const captureCell = useCallback<
    PivotCellExplanationConfig<DatePivotParams>["capture"]
  >(
    ({ columnKey, row, value, viewParams, source }) => {
      if (!supportsCellExplain || !activeRun || !source?.receiptId) {
        return undefined;
      }
      const viewContext = createCommonViewContext({
        surfaceId: "inventory-health",
        measure: viewParams.measure,
        dateHierarchy: viewParams.dateHierarchy,
        minDate: viewParams.minDate,
        maxDate: viewParams.maxDate,
        segments: viewParams.segments,
        filters: viewParams.filters,
        postFilters: viewParams.postFilters,
      });
      const renderedRunIdentity = getRunIdentity(source.run);
      if (
        !activeRunIdentity ||
        !renderedRunIdentity ||
        renderedRunIdentity !== activeRunIdentity
      ) {
        return undefined;
      }
      const selectedMeasure = normalizedMeasure(viewParams.measure);
      const supportsDedicatedUnfulfilledDemand =
        selectedMeasure === UNFULFILLED_DEMAND_MEASURE &&
        supportsCommonNucleusInventoryHealthUnfulfilledDemand(
          activeRun.runSettings.organizationId,
          variant,
        );
      if (supportsDedicatedUnfulfilledDemand) {
        const context = createInventoryHealthUnfulfilledDemandCellContext({
          tenantId: activeRun.runSettings.organizationId,
          variant,
          receiptId: source.receiptId,
          columnKey,
          periodField: viewParams.dateHierarchy,
          periodStartEpochMs: Number(columnKey),
          segments: viewParams.segments,
          row,
          rawValue: value,
          selectedMeasure: viewParams.measure,
          filters: viewParams.filters,
          postFilters: viewParams.postFilters,
          settingsNameAndVersion: source.run.runSettings.settingsNameAndVersion,
          comparison: false,
        });
        if (
          !context ||
          !isSupportedInventoryHealthUnfulfilledDemandCellContext(
            context,
            activeRun.runSettings.organizationId,
          )
        ) {
          return undefined;
        }
        return Object.freeze({
          prompt: inventoryHealthUnfulfilledDemandCellPrompt(context),
          metricContext: context,
          viewContext,
        });
      }
      if (selectedMeasure !== ENDING_INVENTORY_MEASURE) {
        if (
          !selectedMeasure ||
          row.type !== "group" ||
          columnKey === "TOTAL" ||
          normalizedMeasure(row.measure) !== selectedMeasure
        ) {
          return undefined;
        }
        const renderedCellContext = createRenderedCellContext({
          variant,
          receiptId: source.receiptId,
          columnKey,
          row,
          rawValue: value,
          selectedMeasure: viewParams.measure,
        });
        if (
          !renderedCellContext ||
          !isSupportedRenderedCellContext(renderedCellContext)
        ) {
          return undefined;
        }
        return Object.freeze({
          prompt: renderedCellPrompt(
            renderedCellContext,
            viewParams.dateHierarchy,
          ),
          metricContext: renderedCellContext,
          viewContext,
        });
      }
      const receiptBoundContext = createReceiptBoundInventoryHealthCellContext({
        variant,
        receiptId: source.receiptId,
        columnKey,
        row,
        rawValue: value,
        selectedMeasure: viewParams.measure,
        measure: viewParams.measure,
        periodField: viewParams.dateHierarchy,
        periodStartEpochMs: Number(columnKey),
        segments: viewParams.segments,
        filters: viewParams.filters,
        postFilters: viewParams.postFilters,
        settingsNameAndVersion: source.run.runSettings.settingsNameAndVersion,
        comparison: false,
      });
      if (
        !receiptBoundContext ||
        !isSupportedReceiptBoundInventoryHealthCellRecipe(receiptBoundContext)
      ) {
        const renderedCellContext = createRenderedCellContext({
          variant,
          receiptId: source.receiptId,
          columnKey,
          row,
          rawValue: value,
          selectedMeasure: viewParams.measure,
        });
        if (
          !renderedCellContext ||
          !isSupportedRenderedCellContext(renderedCellContext)
        ) {
          return undefined;
        }
        return Object.freeze({
          prompt: renderedCellPrompt(
            renderedCellContext,
            viewParams.dateHierarchy,
          ),
          metricContext: renderedCellContext,
          viewContext,
        });
      }
      return Object.freeze({
        prompt: inventoryHealthReceiptCellPrompt(receiptBoundContext),
        metricContext: receiptBoundContext,
        viewContext,
      });
    },
    [activeRun, activeRunIdentity, supportsCellExplain, variant],
  );

  const explainCell = useCallback(
    (capture: CellExplanationCapture) => {
      const context = capture.metricContext;
      const renderedRun = capture.source.run;
      const renderedRunIdentity = getRunIdentity(renderedRun);
      const sourceIdentity = renderedViewBindingIdentity(capture.source);
      if (
        !supportsCellExplain ||
        !isSupportedExplanationContext(
          context,
          variant,
          renderedRun.runSettings.organizationId,
        ) ||
        context.receiptId !== capture.source.receiptId ||
        !activeRun ||
        !activeRunIdentity ||
        !renderedRunIdentity ||
        renderedRunIdentity !== activeRunIdentity ||
        renderedRun.status !== "RUN_COMPLETED" ||
        renderedRun.runSettings.workflowId !== "workflow_atomic_pivots" ||
        !supportsCommonNucleusInventoryHealthVariant(
          renderedRun.runSettings.organizationId,
          variant,
        ) ||
        (Boolean(selectedCustomerId) &&
          selectedCustomerId !== renderedRun.runSettings.organizationId) ||
        (hasRenderedSourceSignalRef.current &&
          currentRenderedSourceIdentityRef.current !== sourceIdentity)
      ) {
        return rejectNucleusExplainActivation(NUCLEUS_EXPLAIN_STALE_REASON);
      }
      if (isAwaitingResponse || pendingCellRef.current) {
        return rejectNucleusExplainActivation(NUCLEUS_EXPLAIN_BUSY_REASON);
      }

      const nextPendingCell = {
        context,
        viewContext: capture.viewContext,
        prompt: capture.prompt,
        runIdentity: renderedRunIdentity,
        sourceIdentity,
        connectionReady: false,
      } as const;
      if (!hasRenderedSourceSignalRef.current) {
        currentRenderedSourceIdentityRef.current = sourceIdentity;
      }
      setPendingCell(nextPendingCell);
      setIsSidebarOpen(true);
      return awaitNucleusExplainStart(
        () => connectChat(renderedRun, "inventory-health"),
        () => clearPending(nextPendingCell),
        () => {
          if (
            !supportsCellExplainRef.current ||
            activeRunIdentityRef.current !== nextPendingCell.runIdentity ||
            currentRenderedSourceIdentityRef.current !==
              nextPendingCell.sourceIdentity
          ) {
            return false;
          }
          return replacePending(
            nextPendingCell,
            Object.freeze({ ...nextPendingCell, connectionReady: true }),
          );
        },
      );
    },
    [
      activeRun,
      activeRunIdentity,
      clearPending,
      connectChat,
      isAwaitingResponse,
      pendingCellRef,
      replacePending,
      selectedCustomerId,
      setPendingCell,
      setIsSidebarOpen,
      supportsCellExplain,
      variant,
    ],
  );

  useEffect(() => {
    if (!pendingCell) return;
    if (
      !supportsCellExplain ||
      pendingCell.runIdentity !== activeRunIdentity ||
      currentRenderedSourceIdentityRef.current !== pendingCell.sourceIdentity
    ) {
      clearPending(pendingCell);
      return;
    }
    if (
      !pendingCell.connectionReady ||
      !isConnected ||
      isConnecting ||
      isAwaitingResponse
    ) {
      return;
    }

    sendMessage(
      pendingCell.prompt,
      pendingCell.context,
      pendingCell.viewContext,
    );
    clearPending(pendingCell);
  }, [
    activeRunIdentity,
    clearPending,
    isAwaitingResponse,
    isConnected,
    isConnecting,
    pendingCell,
    sendMessage,
    supportsCellExplain,
  ]);

  const cellExplanation = useMemo(
    () =>
      supportsCellExplain
        ? {
            capture: captureCell,
            onExplain: explainCell,
          }
        : undefined,
    [captureCell, explainCell, supportsCellExplain],
  );

  return Object.freeze({
    cellExplanation,
    cellExplanationEnabled: !isAwaitingResponse && !pendingCell,
    onRenderedSourceChange: handleRenderedSourceChange,
  });
};
